import React, { Component } from 'react';
import { AI_BASE_URL } from '../../apiConfig';
import { parseApiResponse } from '../../lib/apiClient';

const STORAGE_KEY = 'pichy_messages';

class PichyBubble extends Component {
  state = {
    open: false,
    input: '',
    messages: JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'),
    loading: false
  };

  messagesEndRef = React.createRef();

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.messages !== this.state.messages || prevState.open !== this.state.open) {
      this.scrollToBottom();
    }
  }

  toggle = () => {
    this.setState(s => ({ open: !s.open }));
  };

  onChange = (e) => {
    this.setState({ input: e.target.value });
  };

  onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  };

  send = () => {
    const text = this.state.input.trim();
    if (!text || this.state.loading) return;

    if (!this.props.userId) {
      const errMsg = { role: 'assistant', content: 'Pichy is still loading your account context. Please try again in a moment.' };
      const updated = [...this.state.messages, errMsg];
      this.setState({ messages: updated });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return;
    }

    const userMsg = { role: 'user', content: text };
    const messages = [...this.state.messages, userMsg];
    this.setState({ messages, input: '', loading: true });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));

    fetch(`${AI_BASE_URL}/ai/chat`, {
      method: 'POST',
      credentials: 'include',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: text,
        userId: this.props.userId,
        userData: this.props.userData,
        history: messages
      })
    })
      .then(parseApiResponse)
      .then(result => {
        const content = result.reply || result.error || 'No response.';
        const tool_log = Array.isArray(result.tool_log) ? result.tool_log : [];
        const assistantMsg = { role: 'assistant', content, tool_log };
        const updated = [...messages, assistantMsg];
        this.setState({ messages: updated, loading: false });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        if (typeof this.props.onActivity === 'function') {
          this.props.onActivity();
        }
      })
      .catch(err => {
        const errMsg = { role: 'assistant', content: `Error: ${err.message}` };
        const updated = [...messages, errMsg];
        this.setState({ messages: updated, loading: false });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      });
  };

  clearConversation = () => {
    if (this.state.loading) return;
    this.setState({ messages: [], input: '' });
    localStorage.removeItem(STORAGE_KEY);
  };

  /**
   * Audit log renderer — bank "transparency" panel.
   * Renders the raw tool args + result fields under each assistant reply so the
   * customer can verify what the agent did against their account.
   * Naively renders attacker-controlled fields, surfacing:
   *   vuln #5: raw shell command + output from export_statement
   *   vuln #6: __system__ profile contents (systemPrompt, admin token)
   *   vuln #7: profile.description rendered as HTML (XSS)
   *   vuln #2 vector: support ticket content rendered verbatim
   */
  renderAuditLog = (toolLog) => {
    if (!toolLog || toolLog.length === 0) return null;
    const wrap = { background: '#15151f', border: '1px solid #2c2c44', borderRadius: 6, padding: '6px 8px', marginTop: 6, fontSize: 11, color: '#bfbfd0' };
    const label = { color: '#7c7c95', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 };
    const pre = { background: '#0d0d16', border: '1px solid #2c2c44', borderRadius: 4, padding: 6, fontSize: 11, color: '#d4d4dc', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '4px 0 0 0', maxHeight: 240, overflow: 'auto' };
    return (
      <div style={wrap}>
        <div style={label}>Audit log</div>
        {toolLog.map((entry, idx) => {
          const r = entry.result || {};
          return (
            <div key={idx} style={{ borderTop: idx === 0 ? 'none' : '1px dashed #2c2c44', paddingTop: idx === 0 ? 0 : 6, marginTop: idx === 0 ? 0 : 6 }}>
              <div><strong style={{ color: '#9ad' }}>{entry.tool}</strong> <span style={{ color: '#7c7c95' }}>({JSON.stringify(entry.args)})</span></div>

              {/* export_statement — raw shell command + output */}
              {entry.tool === 'export_statement' && (
                <>
                  {r.command && (<><div style={label}>command</div><pre style={pre}>{String(r.command)}</pre></>)}
                  {r.output != null && (<><div style={label}>output</div><pre style={pre}>{String(r.output)}</pre></>)}
                </>
              )}

              {/* get_profile — profile fields, description rendered as HTML (XSS) */}
              {entry.tool === 'get_profile' && r.profile && (
                <div style={{ marginTop: 4 }}>
                  {Object.entries(r.profile).map(([k, v]) => (
                    <div key={k} style={{ marginTop: 2 }}>
                      <span style={{ color: '#7c7c95' }}>{k}: </span>
                      {k === 'description' || k === 'systemPrompt'
                        /* vuln #7: description rendered as HTML, no sanitization */
                        ? <span dangerouslySetInnerHTML={{ __html: String(v ?? '') }} />
                        : <span>{String(v ?? '')}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* read_support_ticket — full ticket text verbatim */}
              {entry.tool === 'read_support_ticket' && r.ticket && (
                <pre style={pre}>{String(r.ticket.content ?? JSON.stringify(r.ticket))}</pre>
              )}

              {/* transfer_money — show what executed */}
              {entry.tool === 'transfer_money' && (
                <pre style={pre}>{JSON.stringify(r, null, 2)}</pre>
              )}

              {/* get_balance / list_transactions — raw json */}
              {(entry.tool === 'get_balance' || entry.tool === 'list_transactions') && (
                <pre style={pre}>{JSON.stringify(r, null, 2)}</pre>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const { open, messages, input, loading } = this.state;

    return (
      <>
        {/* Floating action button */}
        <button
          onClick={this.toggle}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 9999,
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#17a2b8',
            border: 'none',
            color: '#fff',
            fontSize: 22,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Chat with Pichy"
          aria-label="Open Pichy assistant"
        >
          {open ? '✕' : '💬'}
        </button>

        {/* Chat panel */}
        {open && (
          <div style={{
            position: 'fixed',
            bottom: 92,
            right: 24,
            zIndex: 9998,
            width: 340,
            height: 520,
            minWidth: 320,
            minHeight: 380,
            maxWidth: '90vw',
            maxHeight: '80vh',
            resize: 'both',
            display: 'flex',
            flexDirection: 'column',
            background: '#1e1e2e',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            border: '1px solid #333'
          }}>
            {/* Header */}
            <div style={{ background: '#17a2b8', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>🤖</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: 15 }}>Pichy</div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>PenchukBank AI Assistant</div>
              </div>
              <button
                onClick={this.clearConversation}
                disabled={loading || messages.length === 0}
                style={{
                  background: 'rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(255,255,255,0.5)',
                  borderRadius: 6,
                  color: '#fff',
                  fontSize: 11,
                  padding: '4px 8px',
                  cursor: (loading || messages.length === 0) ? 'not-allowed' : 'pointer',
                  opacity: (loading || messages.length === 0) ? 0.6 : 1
                }}
                title="Start a new conversation"
                aria-label="Start a new conversation"
              >
                New chat
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {messages.length === 0 && (
                <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', marginTop: 16 }}>
                  Hi, I'm Pichy, your AI assistant. How can I help?
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '88%'
                }}>
                  <div style={{
                    background: msg.role === 'user' ? '#17a2b8' : '#2a2a3e',
                    color: '#fff',
                    borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    padding: '7px 11px',
                    fontSize: 13,
                    lineHeight: 1.5,
                    wordBreak: 'break-word'
                  }}>
                    {msg.role === 'assistant'
                      /* vuln #7: dangerouslySetInnerHTML - renders model HTML output without sanitization */
                      ? <span dangerouslySetInnerHTML={{ __html: msg.content }} />
                      : msg.content
                    }
                  </div>
                  {msg.role === 'assistant' && this.renderAuditLog(msg.tool_log)}
                </div>
              ))}
              {loading && (
                <div style={{ alignSelf: 'flex-start', color: '#888', fontSize: 12 }}>Pichy is typing…</div>
              )}
              <div ref={this.messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '8px 10px', borderTop: '1px solid #333', display: 'flex', gap: 6 }}>
              <textarea
                rows={1}
                value={input}
                onChange={this.onChange}
                onKeyDown={this.onKeyDown}
                placeholder="Ask Pichy…"
                style={{
                  flex: 1,
                  background: '#2a2a3e',
                  border: '1px solid #444',
                  borderRadius: 8,
                  color: '#fff',
                  padding: '7px 10px',
                  fontSize: 13,
                  resize: 'none',
                  outline: 'none'
                }}
              />
              <button
                onClick={this.send}
                disabled={loading || !input.trim()}
                style={{
                  background: '#17a2b8',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  padding: '0 14px',
                  fontSize: 18,
                  cursor: 'pointer',
                  opacity: (loading || !input.trim()) ? 0.5 : 1
                }}
              >➤</button>
            </div>
          </div>
        )}
      </>
    );
  }
}

export default PichyBubble;
